import type { SubmitHandler } from 'react-hook-form';
import { useStreamFunctions } from 'stream/globalFunction/useStreamFunctions';

import type { CreateFunctionCommand as CreateFunctionPayload } from '@polyai/schemas';
import Input from '@polyai/ui/components/atoms/Input';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import type { FunctionType } from 'api/resources/functions/types';
import FormProvider from 'components/molecules/Form';
import { generateDefaultFunctionCode } from 'components/molecules/RichTextInput/utils';
import useFunctionForm from 'components/organisms/EventSourcingFunctionForm/hooks/useFunctionForm';
import type { StreamFunctionForm } from 'components/organisms/EventSourcingFunctionForm/types';
import { validateStreamFunctionName } from 'utils/nameValidators';
import { createFunctionButton } from './FunctionCreatePopup.copy';

interface FunctionsFormProps {
  defaultName?: string;
  onCreate?: (data: { id: string; name: string }) => void;
  onCancel?: () => void;
  functionType?: FunctionType;
  description?: string;
  code?: string;
  title?: string;
  subtitle?: string;
}

const DEFAULT_DESCRIPTION = 'This is a function that was created automatically';

export function FunctionCreatePopup({
  functionType,
  onCreate,
  onCancel,
  description = DEFAULT_DESCRIPTION,
  subtitle = createFunctionButton.subtitle,
  title = createFunctionButton.title,
  defaultName,
  code,
}: FunctionsFormProps) {
  const { methods, defaultValues, saveFunction } = useFunctionForm({
    func: undefined,
    defaultName,
    defaultDescription: description,
    functionType,
  });

  const {
    formState: { errors, isValid },
    register,
    handleSubmit,
  } = methods;

  const { activeFunctions, createFunction } = useStreamFunctions();

  const validate = (name: string) => {
    return validateStreamFunctionName(activeFunctions ?? [])(name);
  };

  const onSubmit: SubmitHandler<StreamFunctionForm> = async data => {
    const payload: CreateFunctionPayload = {
      id: data.id,
      name: data.name,
      code: generateDefaultFunctionCode({
        functionType,
        code,
        functionName: data.name,
      }),
      parameters: [],
      description,
      latencyControl: {
        enabled: false,
        initialDelay: 0,
        interval: 0,
        delayResponses: [],
      },
      references: {
        flowSteps: {},
        topics: {},
        behaviour: {},
        stopKeywords: {},
        variables: {},
      },
      errors: [],
      archived: false,
    };
    void createFunction(payload);
    onCreate?.({ id: data.id, name: data.name });
  };

  return (
    <ConfirmationModal
      buttonText="Create"
      cancelText="Cancel"
      closeOnDocumentClick={false}
      confirmDisabled={!isValid}
      confirmRequesting={false}
      content={
        <FormProvider
          defaultValues={defaultValues}
          methods={methods}
          onSubmit={saveFunction}
        >
          <Input
            autoComplete="off"
            {...register('name', {
              validate,
            })}
            aria-autocomplete="none"
            data-test-id="function-name-input-btn"
            disabled={!!defaultName}
            errors={
              errors.name && errors.name.message ? [errors.name.message] : []
            }
            label="Function name"
            placeholder="Type in"
            fullWidth
            required
          />
        </FormProvider>
      }
      size="md"
      subtitle={subtitle}
      title={title}
      open
      skipCloseAndConfirm
      onCancelButtonClick={onCancel}
      onConfirmButtonClick={handleSubmit(onSubmit)}
    />
  );
}
